import { A, useSearchParams } from "@solidjs/router";
import { createForm } from "@tanstack/solid-form";
import { BiSolidUserAccount } from "solid-icons/bi";
import {
  IoArrowBack,
  IoBriefcaseOutline,
  IoBusinessOutline,
  IoSearchOutline,
} from "solid-icons/io";
import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  Match,
  onCleanup,
  Show,
  Switch,
  useContext,
} from "solid-js";
import { createStore, produce } from "solid-js/store";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { extractApiFormError } from "@/api/utils/error";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import {
  Checkbox,
  CheckboxControl,
  CheckboxDescription,
} from "@/components/ui/checkbox";
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxTrigger,
} from "@/components/ui/combobox";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  Input,
} from "@/components/ui/input";
import {
  DEFAULT_NATIONALITY_OPTION,
  selectCountryOptions,
} from "@/constants/nationality-flags";
import { useSendVerificationEmail, useSignup } from "@/domains/auth/api";
import {
  AuthContainer,
  AuthDescription,
  AuthFormBox,
  AuthHeader,
  AuthTitle,
} from "@/domains/auth/components/AuthBoxes";
import {
  DEFAULT_DRAFT,
  getSignupDraft,
  type SignupIntent,
  type SignupPublisherKind,
  type SignupStep,
  setSignupDraft,
  toSignupIntentWireValue,
} from "@/domains/auth/store/signup-draft-store";
import { MetaTitleWithSeparator } from "@/domains/business/seo";
import { usePostHog } from "@/hooks/usePostHog";
import { getSafeRedirectPath } from "@/libs/redirect";
import { tryCatch } from "@/libs/try-catch";

type SignupRouteProps = {
  step: SignupStep;
};

type FieldErrorsKey = "email" | "name" | "location" | "nationality";

interface SignupState extends SignupRouteProps {
  signupIntent: SignupIntent | null;
  publisherKind: SignupPublisherKind | null;
  email: string | null;
  error: string | null;
  fieldErrors: Partial<Record<FieldErrorsKey, string[]>> | null;
}

const createSignupStore = () => {
  const [searchParams] = useSearchParams();
  const emailFromParams =
    typeof searchParams.email === "string" ? searchParams.email : null;
  const draft = getSignupDraft();
  const initialState: Partial<SignupState> = draft
    ? {
        step: draft.step,
        signupIntent: draft.signupIntent,
        publisherKind: draft.publisherKind,
        email: emailFromParams ?? draft.email,
      }
    : { step: "intent", email: emailFromParams };
  return createStore<SignupState>({
    signupIntent: null,
    publisherKind: null,
    email: emailFromParams,
    error: null,
    fieldErrors: null,
    step: "intent",
    ...initialState,
  });
};

const SignUpContext = createContext<ReturnType<typeof createSignupStore>>();

function useSignupStore<_T>() {
  const store = useContext(SignUpContext);
  if (!store) {
    throw new Error("useSignupStore must be used within a SignUpProvider");
  }
  return store;
}

const Route = () => {
  const store = createSignupStore();
  const { t } = useI18n();
  return (
    <AuthLayout>
      <SignUpContext.Provider value={store}>
        <MetaTitleWithSeparator>
          {t("auth.signup.meta.title")}
        </MetaTitleWithSeparator>
        <AuthContainer aria-live="polite">
          <Switch>
            <Match when={store[0].step === "intent"}>
              <IntentSelector />
            </Match>
            <Match when={store[0].step === "publisher-kind"}>
              <PublisherKindSelector />
            </Match>
            <Match when={store[0].step === "signup"}>
              <SignUp />
            </Match>
            <Match when={store[0].step === "email-sent"}>
              <EmailSent />
            </Match>
          </Switch>
        </AuthContainer>
      </SignUpContext.Provider>
    </AuthLayout>
  );
};

const signupSchema = z.object({
  email: z.email("L'email non è valida"),
  full_name: z.string().min(1, "Il nome completo è obbligatorio"),
  location: z.string().min(1, "La nazionalità è obbligatoria"),
  accept_terms: z.boolean().refine((value) => value, {
    message: "Devi accettare i termini e le condizioni d'uso",
  }),
  send_email: z.boolean().optional(),
});

type Language = {
  value: string;
  label: string;
};

const SignUp = () => {
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const { capture, identify } = usePostHog();

  const { mutateAsync: signup } = useSignup();
  const [store, setStore] = useSignupStore();
  const [, setLoading] = createSignal(false);

  const draft = getSignupDraft();
  const emailFromParams =
    typeof searchParams.email === "string" ? searchParams.email : null;

  const form = createForm(() => ({
    defaultValues: {
      email: emailFromParams ?? store.email ?? draft?.email ?? "",
      full_name: draft?.full_name ?? "",
      location: draft?.location ?? "IT",
      send_email: draft?.send_email ?? false,
      accept_terms: draft?.accept_terms ?? false,
    },
    onSubmit: async ({ value }) => {
      setStore(
        produce((state) => {
          state.error = null;
          state.fieldErrors = null;
        }),
      );
      const validatedData = signupSchema.safeParse(value);
      if (!validatedData.success) {
        console.error(validatedData.error);
        return validatedData.error.issues[0].message;
      }
      setLoading(true);
      // Sanitize before sending: the backend rejects non-relative destinations,
      // and we should never be the one handing it a hostile value.
      const next =
        typeof searchParams.next === "string"
          ? getSafeRedirectPath(searchParams.next, "/")
          : undefined;
      const [response, error] = await tryCatch(
        signup({
          email: validatedData.data.email,
          name: validatedData.data.full_name,
          nationality: validatedData.data.location,
          next,
          ...(store.signupIntent === "find_projects" && {
            newsletterOptIn: validatedData.data.send_email ?? false,
          }),
          // L'unico momento in cui l'intento è noto: da qui in poi vive sul
          // profilo, non più solo nel draft locale che `VerifyRoute` cancella.
          signupIntent: toSignupIntentWireValue(
            store.signupIntent,
            store.publisherKind,
          ),
        }),
      );
      if (error) {
        const { fieldErrors, error: errorMsg } = extractApiFormError(error);
        setStore(
          produce((state) => {
            state.fieldErrors = fieldErrors;
            state.error = errorMsg;
          }),
        );
      } else {
        setStore("step", "email-sent");
        setStore("email", validatedData.data.email);
        setSignupDraft({
          step: "email-sent",
          signupIntent: store.signupIntent,
          publisherKind: store.publisherKind,
          email: validatedData.data.email,
          full_name: validatedData.data.full_name,
          location: validatedData.data.location,
          send_email: validatedData.data.send_email ?? false,
          accept_terms: validatedData.data.accept_terms,
        });
        identify(response.profile.id, {
          email: validatedData.data.email,
          name: validatedData.data.full_name,
          nationality: validatedData.data.location,
          signupIntent: store.signupIntent,
          publisherKind: store.publisherKind,
          onboarding_status: response.profile.onboarding_status,
        });
        capture("create_account", {
          email: validatedData.data.email,
          name: validatedData.data.full_name,
          nationality: validatedData.data.location,
          signupIntent: store.signupIntent,
          publisherKind: store.publisherKind,
        });
      }
      setLoading(false);
    },
    schema: signupSchema,
  }));

  const memoizedLanguages = createMemo(() => {
    return selectCountryOptions();
  });

  return (
    <AuthFormBox>
      <Button
        variant="ghost"
        class="w-fit"
        onClick={() =>
          setStore(
            "step",
            store.signupIntent === "publish" ? "publisher-kind" : "intent",
          )
        }
      >
        <IoArrowBack size={16} class="mr-2" />
        {t("auth.signup.back")}
      </Button>
      <SignupProgress step={3} />
      <AuthHeader>
        <Switch>
          <Match when={store.signupIntent === "find_projects"}>
            <AuthTitle>{t("auth.signup.findProjects.title")}</AuthTitle>
          </Match>
          <Match when={store.publisherKind === "solo_freelancer"}>
            <AuthTitle>{t("auth.signup.publisher.freelancer.title")}</AuthTitle>
          </Match>
          <Match when={store.publisherKind === "company"}>
            <AuthTitle>{t("auth.signup.publisher.company.title")}</AuthTitle>
          </Match>
        </Switch>
        <AuthDescription>
          {t("auth.signup.magicLinkDescription")}
        </AuthDescription>

        <Show when={store.error}>
          <p class="text-red-500">{store.error}</p>
        </Show>
      </AuthHeader>

      <div class="flex flex-col w-full justify-between gap-4">
        <form.Field
          name="email"
          validators={{ onChange: signupSchema.shape.email }}
        >
          {(field) => (
            <FieldContainer>
              <FieldContainerLabel for="email">Email</FieldContainerLabel>
              <Input
                id={field().name}
                name={field().name}
                value={field().state.value ?? ""}
                onInput={(e) => {
                  field().handleChange(e.target.value);
                  if (store.fieldErrors?.email) {
                    setStore(
                      produce((s) => {
                        if (s.fieldErrors) delete s.fieldErrors.email;
                      }),
                    );
                  }
                }}
                onBlur={() => field().handleBlur()}
                onChange={(e) => field().handleChange(e.target.value)}
                placeholder={t("auth.signup.emailPlaceholder")}
              />
              <FieldInfo field={field()} />
              <Show when={store.fieldErrors?.email?.[0]}>
                <p class="text-red-500 text-xs">
                  {store.fieldErrors?.email?.[0]}
                </p>
              </Show>
            </FieldContainer>
          )}
        </form.Field>
        <form.Field
          name="full_name"
          validators={{ onChange: signupSchema.shape.full_name }}
        >
          {(field) => (
            <FieldContainer>
              <FieldContainerLabel for="full_name">
                Nome completo
              </FieldContainerLabel>
              <Input
                id={field().name}
                name={field().name}
                value={field().state.value ?? ""}
                onInput={(e) => {
                  field().handleChange(e.target.value);
                  if (store.fieldErrors?.name) {
                    setStore(
                      produce((s) => {
                        if (s.fieldErrors) delete s.fieldErrors.name;
                      }),
                    );
                  }
                }}
                onBlur={() => field().handleBlur()}
                onChange={(e) => field().handleChange(e.target.value)}
                placeholder={t("auth.signup.fullNamePlaceholder")}
              />
              <FieldInfo field={field()} />
              <Show when={store.fieldErrors?.name?.[0]}>
                <p class="text-red-500 text-xs">
                  {store.fieldErrors?.name?.[0]}
                </p>
              </Show>
            </FieldContainer>
          )}
        </form.Field>
        <form.Field
          name="location"
          validators={{
            onChange: signupSchema.shape.location,
          }}
        >
          {(field) => (
            <FieldContainer>
              <FieldContainerLabel for="nationality">
                {t("profile.edit.dialog.nationality")}
              </FieldContainerLabel>
              <Combobox<Language>
                options={memoizedLanguages()}
                defaultValue={
                  field().state.value ? DEFAULT_NATIONALITY_OPTION : undefined
                }
                optionValue={(option) => option.value}
                optionLabel={(option) => option.label}
                class="bg-white"
                optionTextValue={(option) => option.label}
                onChange={(value) => {
                  field().setValue(value?.value ?? "");
                  if (store.fieldErrors?.nationality) {
                    setStore(
                      produce((s) => {
                        if (s.fieldErrors) delete s.fieldErrors.nationality;
                      }),
                    );
                  }
                }}
                itemComponent={(props) => (
                  <ComboboxItem {...props}>{props.item.textValue}</ComboboxItem>
                )}
              >
                <ComboboxTrigger class="h-10">
                  <ComboboxInput placeholder="Seleziona la tua nazionalità" />
                </ComboboxTrigger>
                <ComboboxContent class="max-h-60" />
              </Combobox>
              <FieldInfo field={field()} />
              <Show when={store.fieldErrors?.nationality?.[0]}>
                <p class="text-red-500 text-xs">
                  {store.fieldErrors?.nationality?.[0]}
                </p>
              </Show>
            </FieldContainer>
          )}
        </form.Field>
        <Show when={store.signupIntent === "find_projects"}>
          <form.Field name="send_email" validators={{ onChange: z.boolean() }}>
            {(field) => (
              <Checkbox
                class="flex items-center gap-2"
                checked={field().state.value ?? false}
                onChange={(checked) => field().setValue(checked)}
              >
                <CheckboxControl />
                <CheckboxDescription class="text-sm text-muted-foreground">
                  {t("auth.signup.sendJobEmails")}
                </CheckboxDescription>
              </Checkbox>
            )}
          </form.Field>
        </Show>
        <form.Field name="accept_terms" validators={{ onChange: z.boolean() }}>
          {(field) => (
            <Checkbox
              class="mt-2 flex items-center gap-2"
              checked={field().state.value ?? false}
              onChange={(checked) => field().setValue(checked)}
            >
              <CheckboxControl />
              <CheckboxDescription class="text-sm text-muted-foreground">
                {t("auth.signup.accept_terms_prefix")}
                <a
                  href="https://www.sevedemo.com/termini/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline text-primary hover:text-primary/80"
                >
                  {t("auth.signup.terms_and_conditions_link")}
                </a>
                {t("auth.signup.accept_terms_connector")}
                <a
                  href="https://www.sevedemo.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline text-primary hover:text-primary/80"
                >
                  {t("auth.signup.privacy_policy_link")}
                </a>
              </CheckboxDescription>
            </Checkbox>
          )}
        </form.Field>
      </div>
      <form.Subscribe
        selector={(state) => ({
          values: state.values,
          step: store.step,
          signupIntent: store.signupIntent,
          publisherKind: store.publisherKind,
          email: store.email,
        })}
      >
        {(getSnapshot) => {
          createEffect(() => {
            const snap = getSnapshot();
            if (!snap || snap.step === "email-sent") return;
            const values = snap.values as {
              full_name?: string;
              location?: string;
              send_email?: boolean;
              accept_terms?: boolean;
            };
            setSignupDraft({
              step: snap.step,
              signupIntent: snap.signupIntent,
              publisherKind: snap.publisherKind,
              email: snap.email,
              full_name:
                snap.step === "signup" ? (values?.full_name ?? "") : "",
              location:
                snap.step === "signup" ? (values?.location ?? "IT") : "IT",
              send_email:
                snap.step === "signup" ? (values?.send_email ?? false) : false,
              accept_terms:
                snap.step === "signup"
                  ? (values?.accept_terms ?? false)
                  : false,
            });
          });
          return null;
        }}
      </form.Subscribe>
      <form.Subscribe
        selector={(state) => ({
          isValid: state.isValid,
          isSubmitting: state.isSubmitting,
          isSubmittable:
            state.values.accept_terms &&
            state.values.email.length > 0 &&
            state.values.full_name.length > 0 &&
            state.values.location.length > 0,
        })}
      >
        {(state) => (
          <Button
            type="submit"
            class="w-full"
            aria-live="polite"
            size="xl"
            disabled={
              !state().isValid || state().isSubmitting || !state().isSubmittable
            }
            onClick={() => form.handleSubmit()}
          >
            <Show
              when={state().isSubmitting}
              fallback={<span>{t("auth.signup.createAccount")}</span>}
            >
              {t("auth.signup.loading")}
            </Show>
          </Button>
        )}
      </form.Subscribe>
    </AuthFormBox>
  );
};

const SignupProgress = (props: { step: 1 | 2 | 3 }) => {
  return (
    <div class="flex gap-2">
      {[1, 2, 3].map((step) => (
        <div
          class="h-1.5 w-12 rounded-full"
          classList={{
            "bg-primary": step === props.step,
            "bg-primary/30": step < props.step,
            "bg-muted": step > props.step,
          }}
        />
      ))}
    </div>
  );
};

const SignupOptionCard = (props: {
  title: string;
  description: string;
  selected: boolean;
  icon: "publish" | "search" | "company" | "freelancer";
  onClick: () => void;
}) => {
  const Icon = () => {
    switch (props.icon) {
      case "publish":
        return <IoBriefcaseOutline class="size-6 text-primary" />;
      case "search":
        return <IoSearchOutline class="size-6 text-amber-700" />;
      case "company":
        return <IoBusinessOutline class="size-6 text-primary" />;
      case "freelancer":
        return <BiSolidUserAccount class="size-6 text-amber-700" />;
    }
  };

  return (
    <button
      type="button"
      class="relative flex min-h-44 flex-1 flex-col items-start rounded-lg border bg-white p-5 text-left transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      classList={{
        "border-primary bg-primary/5": props.selected,
        "border-border": !props.selected,
      }}
      onClick={props.onClick}
    >
      <div
        class="mb-6 flex size-12 items-center justify-center rounded-lg"
        classList={{
          "bg-primary/10": props.icon === "publish" || props.icon === "company",
          "bg-amber-100":
            props.icon === "search" || props.icon === "freelancer",
        }}
      >
        <Icon />
      </div>
      <div
        class="absolute right-5 top-5 size-7 rounded-full border-2"
        classList={{
          "border-primary bg-primary": props.selected,
          "border-muted-foreground/30": !props.selected,
        }}
      />
      <div class="flex flex-col items-start pr-8">
        <p class="text-lg font-semibold leading-snug text-foreground">
          {props.title}
        </p>
        <p class="mt-2 text-sm leading-6 text-muted-foreground">
          {props.description}
        </p>
      </div>
    </button>
  );
};

const IntentSelector = () => {
  const [store, setStore] = useSignupStore();
  const { t } = useI18n();

  createEffect(() => {
    setSignupDraft({
      ...DEFAULT_DRAFT,
      step: "intent",
      signupIntent: store.signupIntent,
      publisherKind: store.publisherKind,
      email: store.email,
    });
  });

  return (
    <AuthFormBox>
      <SignupProgress step={1} />
      <AuthHeader>
        <AuthTitle>{t("auth.signup.title")}</AuthTitle>
        <AuthDescription>{t("auth.signup.description")}</AuthDescription>
      </AuthHeader>
      <div class="grid gap-4 md:grid-cols-2">
        <SignupOptionCard
          title={t("auth.signup.intent.publish.title")}
          description={t("auth.signup.intent.publish.description")}
          selected={store.signupIntent === "publish"}
          icon="publish"
          onClick={() => setStore("signupIntent", "publish")}
        />
        <SignupOptionCard
          title={t("auth.signup.intent.findProjects.title")}
          description={t("auth.signup.intent.findProjects.description")}
          selected={store.signupIntent === "find_projects"}
          icon="search"
          onClick={() => {
            setStore("signupIntent", "find_projects");
            setStore("publisherKind", null);
          }}
        />
      </div>
      <Button
        disabled={!store.signupIntent}
        size="xl"
        onClick={() =>
          setStore(
            "step",
            store.signupIntent === "publish" ? "publisher-kind" : "signup",
          )
        }
      >
        {t("common.continue")}
      </Button>
      <div class="flex flex-col gap-2">
        <p class="text-muted-foreground text-center text-sm">
          {t("auth.signup.haveAccount")}{" "}
          <A class="underline text-primary hover:text-primary/80" href="/auth">
            {t("auth.signup.login")}
          </A>
        </p>
      </div>
    </AuthFormBox>
  );
};

const PublisherKindSelector = () => {
  const [store, setStore] = useSignupStore();
  const { t } = useI18n();

  createEffect(() => {
    setSignupDraft({
      ...DEFAULT_DRAFT,
      step: "publisher-kind",
      signupIntent: "publish",
      publisherKind: store.publisherKind,
      email: store.email,
    });
  });

  return (
    <AuthFormBox>
      <Button
        variant="ghost"
        class="w-fit"
        onClick={() => setStore("step", "intent")}
      >
        <IoArrowBack size={16} class="mr-2" />
        {t("auth.signup.back")}
      </Button>
      <SignupProgress step={2} />
      <AuthHeader>
        <AuthTitle>{t("auth.signup.publisher.title")}</AuthTitle>
        <AuthDescription>
          {t("auth.signup.publisher.description")}
        </AuthDescription>
      </AuthHeader>
      <div class="grid gap-4 md:grid-cols-2">
        <SignupOptionCard
          title={t("auth.signup.publisher.company.title")}
          description={t("auth.signup.publisher.company.description")}
          selected={store.publisherKind === "company"}
          icon="company"
          onClick={() => setStore("publisherKind", "company")}
        />
        <SignupOptionCard
          title={t("auth.signup.publisher.freelancer.title")}
          description={t("auth.signup.publisher.freelancer.description")}
          selected={store.publisherKind === "solo_freelancer"}
          icon="freelancer"
          onClick={() => setStore("publisherKind", "solo_freelancer")}
        />
      </div>
      <Button
        disabled={!store.publisherKind}
        size="xl"
        onClick={() => setStore("step", "signup")}
      >
        {t("common.continue")}
      </Button>
      <div class="flex flex-col gap-2">
        <p class="text-muted-foreground text-center text-sm">
          {t("auth.signup.haveAccount")}{" "}
          <A class="underline text-primary hover:text-primary/80" href="/auth">
            {t("auth.signup.login")}
          </A>
        </p>
      </div>
    </AuthFormBox>
  );
};

const RESEND_COOLDOWN_SECONDS = 60;

const EmailSent = () => {
  const [store] = useSignupStore();
  const { t } = useI18n();
  const { capture } = usePostHog();
  const { mutateAsync: sendVerificationEmail, isPending } =
    useSendVerificationEmail();
  const [cooldownSeconds, setCooldownSeconds] = createSignal(0);
  let cooldownIntervalId: ReturnType<typeof setInterval> | null = null;

  onCleanup(() => {
    if (cooldownIntervalId !== null) clearInterval(cooldownIntervalId);
  });

  const handleResendEmail = () => {
    if (cooldownSeconds() > 0) return;
    capture("send_verification_email", {
      email: store.email ?? "",
      resend: true,
    });
    sendVerificationEmail({ email: store.email ?? "" }).then(() => {
      setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      if (cooldownIntervalId !== null) clearInterval(cooldownIntervalId);
      cooldownIntervalId = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            if (cooldownIntervalId !== null) {
              clearInterval(cooldownIntervalId);
              cooldownIntervalId = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    });
  };

  const isResendDisabled = () => cooldownSeconds() > 0 || isPending;

  return (
    <AuthFormBox>
      <img
        src="https://bucket.sevedemo.com/assets/sent-email.png"
        alt="Email sent"
        class="w-1/3 mx-auto"
      />
      <AuthHeader class="items-center">
        <AuthTitle class="text-center">
          {t("auth.signup.verifyEmail")}
        </AuthTitle>
        <AuthDescription class="text-center">
          {t("auth.signup.verificationEmailSent")}{" "}
          <span class="font-medium">{store.email}</span>.
        </AuthDescription>
        <AuthDescription class="text-center">
          {t("auth.signup.checkEmailForLink")}
        </AuthDescription>
      </AuthHeader>
      <Button
        variant="outline"
        class="w-fit mx-auto"
        onClick={handleResendEmail}
        disabled={isResendDisabled()}
      >
        <Show
          when={cooldownSeconds() > 0}
          fallback={
            isPending ? t("auth.signup.loading") : t("auth.signup.resendEmail")
          }
        >
          {t("auth.signup.resendEmailCooldown", {
            seconds: cooldownSeconds(),
          })}
        </Show>
      </Button>
    </AuthFormBox>
  );
};

export default Route;
